import { Menu } from "@mantine/core";

import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { shadows } from "#app/theme/v2/shadows";
import { spacing } from "#app/theme/v2/spacing";
import { DROPDOWN_Z_INDEX } from "#app/theme/v2/zIndex";
import { Text } from "#components/atoms/Typographies/Text";

import classes from "./DropdownMenu.module.css";

type DropdownMenuItemVariant = "default" | "destructive";

const ITEM_TEXT_COLORS: Record<DropdownMenuItemVariant, string> = {
  default: colors.text.primaryDefault,
  destructive: colors.text.errorDefault,
};

type DropdownMenuItemProps = {
  children: React.ReactNode;
  variant?: DropdownMenuItemVariant;
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
};

const DropdownMenuItem = ({
  children,
  variant = "default",
  leftSection,
  rightSection,
  onClick,
  disabled,
}: DropdownMenuItemProps) => (
  <Menu.Item
    leftSection={leftSection}
    rightSection={rightSection}
    onClick={onClick}
    disabled={disabled}
    className={classes.item}
  >
    <Text size="small" type="regular" color={ITEM_TEXT_COLORS[variant]}>
      {children}
    </Text>
  </Menu.Item>
);

type DropdownMenuDropdownProps = {
  children: React.ReactNode;
  style?: React.CSSProperties;
};

const DropdownMenuDropdown = ({ children, style }: DropdownMenuDropdownProps) => (
  <Menu.Dropdown
    style={{
      ...style,
      backgroundColor: colors.bg.elevation.primary,
      borderRadius: radius.md,
      boxShadow: shadows.elevation4,
      padding: spacing[2],
      border: "none",
      "--menu-item-hover": colors.bg.button.tertiary.hover,
      "--dropdown-item-radius": radius.xs,
    }}
  >
    {children}
  </Menu.Dropdown>
);

type DropdownMenuProps = {
  children?: React.ReactNode;
  opened?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  position?:
    | "bottom"
    | "bottom-start"
    | "bottom-end"
    | "top"
    | "top-start"
    | "top-end"
    | "left"
    | "right";
  offset?: number;
};

const DropdownMenu = ({
  children,
  opened,
  onOpen,
  onClose,
  position = "bottom-end",
  offset,
}: DropdownMenuProps) => (
  <Menu
    opened={opened}
    onOpen={onOpen}
    onClose={onClose}
    position={position}
    offset={offset}
    zIndex={DROPDOWN_Z_INDEX}
  >
    {children}
  </Menu>
);

DropdownMenu.Target = Menu.Target;
DropdownMenu.Dropdown = DropdownMenuDropdown;
DropdownMenu.Item = DropdownMenuItem;

export { DropdownMenu };
